import { capture } from '@parcel/analytics/browser';
import { Button, cn, toast } from '@parcel/ui';
import { Check, Loader2, Star } from 'lucide-react';
import {
  type WatchDataset,
  type WatchWriteResult,
  useWatchMutations,
  useWatchStatus,
} from '../../lib/workspace-views.js';
import { Hint } from '../hint.js';

interface WatchRecordButtonProps {
  workspaceId: string | undefined;
  dataset: WatchDataset;
  recordId: string;
  source?: 'drawer' | 'toolbar';
}

/** One click follows (adds the record to the Following View); one click on Following unfollows. A record followed
 * only through a list View shows Following, and a click stars it as well. */
export function WatchRecordButton({
  workspaceId,
  dataset,
  recordId,
  source = 'drawer',
}: WatchRecordButtonProps) {
  const status = useWatchStatus(workspaceId, dataset, recordId);
  const { follow, unfollow } = useWatchMutations(workspaceId, dataset);
  const busy = follow.isPending || unfollow.isPending;
  // While a write runs, the button already shows where it is going, with a spinner in place of the star.
  const followed = follow.isPending
    ? true
    : unfollow.isPending
      ? false
      : Boolean(status.data?.followed);
  const inFollowingView = status.data?.system_view_id != null;
  const listViews = (status.data?.via ?? []).filter((entry) => entry.system_kind === null);

  const track = (didFollow: boolean) => ({
    onSuccess: (result: WatchWriteResult) => {
      if (result.changed > 0) {
        capture('record_followed', { record_type: dataset, followed: didFollow, count: 1, source });
      }
      // Unfollowed here, but a followed list View still holds it: say so rather than look broken.
      const still = result.still_followed?.find((entry) => entry.record_id === recordId);
      if (!didFollow && still && still.via.length > 0) {
        toast(
          `Still in your News feed through ${still.via.map((entry) => entry.name).join(', ')}.`,
        );
      }
    },
  });

  const onClick = () => {
    if (!followed) {
      follow.mutate([recordId], track(true));
      return;
    }
    if (inFollowingView) {
      unfollow.mutate([recordId], track(false));
      return;
    }
    // Followed only through a list View: a click stars it too, so it stays followed if that list changes.
    follow.mutate([recordId], track(true));
  };

  return (
    <Hint
      label={
        status.isError
          ? "Couldn't load whether this is followed"
          : !followed
            ? 'Click to add to News feed'
            : inFollowingView || busy
              ? 'Click to remove from News feed'
              : `Following through ${listViews.map((entry) => entry.name).join(', ')} · click to follow it directly too`
      }
    >
      {/* The span keeps the tooltip on a disabled button, which takes no pointer events. */}
      <span className="inline-flex">
        <Button
          type="button"
          // Following reads as on: the filled button and a check; no icon is ever filled.
          variant={followed ? 'secondary' : 'outline'}
          size="sm"
          className={cn('h-8 shrink-0 gap-1.5', busy && 'disabled:opacity-100')}
          aria-pressed={followed}
          aria-busy={busy}
          disabled={!workspaceId || status.isPending || status.isError || busy}
          onClick={onClick}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden />
          ) : followed ? (
            <Check className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Star className="h-3.5 w-3.5" aria-hidden />
          )}
          {followed ? 'Following' : 'Follow'}
        </Button>
      </span>
    </Hint>
  );
}
